import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Badge, Banner, Button, DataList, Heading, LoadingAnimation } from "@knowtex-ai/components";
import { ArrowLeft, Folder, Trash, Xmark } from "iconoir-react";
import FoiaPageShell from "./FoiaPageShell";
import FoiaTimeline from "./FoiaTimeline";
import FoiaStepAction from "./FoiaStepAction";
import FoiaAppointmentsPanel from "./FoiaAppointmentsPanel";
import { fetchFoiaRequest, postFoiaAction } from "./api";
import { CLOSE_REQUEST_ACTION_CODE, DELETION_REQUEST_ACTION_CODE, STEP_KEY_TO_ACTION_CODE } from "./foiaActionCodes";
import { useToast } from "../../hooks/use-toast";
import { getStepDefinition, getStepsForBranch } from "./foiaSteps";
import { formatDate, getNextActionText, getStatusChip, isClosed } from "./foiaUtils";

export default function FoiaRequestDetail({ setAuth, setUserOrg }) {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState("internal");
  const [confirmingClose, setConfirmingClose] = useState(false);
  const [confirmingDeletion, setConfirmingDeletion] = useState(false);

  const reload = useCallback(() => {
    return fetchFoiaRequest(requestId).then((data) => setRequest(data?.request || null));
  }, [requestId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    reload()
      .catch((err) => {
        if (!cancelled) {
          toast({ title: "Couldn't load this FOIA request", description: err.message, variant: "destructive" });
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  const handleStepComplete = (stepKey) => {
    const actionCode = STEP_KEY_TO_ACTION_CODE[stepKey];
    if (!actionCode) return Promise.resolve();
    return postFoiaAction(requestId, { actionCode })
      .then((result) => {
        if (result?.applied === false) {
          toast({ title: "That step was already recorded", description: "Refreshing with the latest state." });
        }
        return reload();
      })
      .catch((err) => {
        toast({ title: "Couldn't record that step", description: err.message, variant: "destructive" });
      });
  };

  const handleCloseRequest = () =>
    postFoiaAction(requestId, { actionCode: CLOSE_REQUEST_ACTION_CODE })
      .then((result) => {
        if (result?.applied === false) {
          toast({ title: "This request was already closed", description: "Refreshing with the latest state." });
        }
        setConfirmingClose(false);
        return reload();
      })
      .catch((err) => {
        toast({ title: "Couldn't close this request", description: err.message, variant: "destructive" });
      });

  const handleRequestDeletion = () =>
    postFoiaAction(requestId, { actionCode: DELETION_REQUEST_ACTION_CODE })
      .then((result) => {
        if (result?.applied === false) {
          toast({ title: "Deletion was already requested", description: "Refreshing with the latest state." });
        }
        setConfirmingDeletion(false);
        return reload();
      })
      .catch((err) => {
        toast({ title: "Couldn't request deletion", description: err.message, variant: "destructive" });
      });

  if (loading) {
    return (
      <FoiaPageShell setAuth={setAuth} setUserOrg={setUserOrg}>
        <div className="flex flex-col items-center justify-center gap-3 py-24 text-gray-400">
          <LoadingAnimation size={28} />
          <span className="text-sm">Loading FOIA request…</span>
        </div>
      </FoiaPageShell>
    );
  }

  if (!request) {
    return (
      <FoiaPageShell setAuth={setAuth} setUserOrg={setUserOrg}>
        <Banner variant="warning" heading="Request not found">
          We couldn't find a FOIA request with that id.
          <div className="mt-3">
            <Button variant="secondary" size="small" onClick={() => navigate("/foia")}>
              Back to FOIA requests
            </Button>
          </div>
        </Banner>
      </FoiaPageShell>
    );
  }

  const status = getStatusChip(request);
  const steps = getStepsForBranch(request.branch);
  const isInternal = mode === "internal";
  const canCloseRequest = isInternal && !isClosed(request) && request.branch !== "cannotRetain";
  const canRequestDeletion =
    isInternal &&
    request.branch === "retention" &&
    !request.deletionRequested &&
    request.currentStepKey !== "deletion_requested";

  return (
    <FoiaPageShell setAuth={setAuth} setUserOrg={setUserOrg}>
      <button
        type="button"
        onClick={() => navigate("/foia")}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800 mb-4"
      >
        <ArrowLeft width={15} height={15} />
        All FOIA requests
      </button>

      <div>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <Heading as="h1" size="large">
              {request.trackingNumber}
            </Heading>
            {isInternal && (
              <p className="mt-1 text-sm text-gray-600">
                {request.patient.name} <span className="text-gray-400">· MRN {request.patient.mrn}</span>
              </p>
            )}
          </div>
          {(canRequestDeletion || canCloseRequest) && (
            <div className="flex flex-wrap items-center gap-2">
              {canRequestDeletion && (
                <Button variant="secondary" size="small" icon={Trash} onClick={() => setConfirmingDeletion(true)}>
                  Request deletion
                </Button>
              )}
              {canCloseRequest && (
                <Button variant="secondary-danger" size="small" icon={Xmark} onClick={() => setConfirmingClose(true)}>
                  Close request
                </Button>
              )}
            </div>
          )}
        </div>

        {confirmingDeletion && canRequestDeletion && (
          <Banner variant="warning" heading="Log the COR's deletion request?" className="mt-5">
            This sends a deletion task to Engineering and stops any unfinished retention steps.
            <div className="mt-3 flex items-center gap-3">
              <Button size="small" onClickAsync={handleRequestDeletion}>
                Confirm deletion request
              </Button>
              <Button size="small" variant="secondary" onClick={() => setConfirmingDeletion(false)}>
                Cancel
              </Button>
            </div>
          </Banner>
        )}

        {confirmingClose && canCloseRequest && (
          <Banner variant="warning" heading="Close this request?" className="mt-5">
            This ends the request before it is completed. Any remaining steps will not be done.
            <div className="mt-3 flex items-center gap-3">
              <Button size="small" variant="primary-danger" onClickAsync={handleCloseRequest}>
                Confirm close request
              </Button>
              <Button size="small" variant="secondary" onClick={() => setConfirmingClose(false)}>
                Cancel
              </Button>
            </div>
          </Banner>
        )}

        <div className="mt-5 rounded-xl border border-gray-200 bg-white">
          <DataList
            size="small"
            items={[
              { id: "due", label: "Due date", value: formatDate(request.dueDate) },
              {
                id: "folder",
                label: "SharePoint folder",
                value: (
                  <span className="inline-flex items-center gap-1.5 font-mono">
                    <Folder width={14} height={14} className="text-gray-400" />
                    {request.sharepointFolderName}
                  </span>
                ),
              },
              { id: "status", label: "Status", value: <Badge variant={status.variant}>{status.label}</Badge> },
              ...(request.corCommunication
                ? [
                    {
                      id: "cor-communication",
                      label: "COR communication",
                      value:
                        request.corCommunication.status === "sent"
                          ? `Deletion email sent on ${formatDate(request.corCommunication.sentAt)}`
                          : "Not yet sent",
                    },
                  ]
                : []),
            ]}
          />
        </div>

        {isInternal && <FoiaAppointmentsPanel appointments={request.appointments} />}

        <Banner variant="info" className="mt-5" showIcon>
          <strong>Next action:</strong> {getNextActionText(request)}
        </Banner>

        {/* View-only for now. When this moves outside this repo, filter the external view on the server (omit patient name, MRN and internal events) as the export endpoint does. */}
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            role="switch"
            aria-checked={isInternal}
            aria-label="Internal view"
            className="foia-switch"
            onClick={() => setMode(isInternal ? "external" : "internal")}
          >
            <span className="foia-switch__track">
              <span className="foia-switch__thumb" />
            </span>
            <span className="foia-switch__text">
              {isInternal ? "Internal view (everything)" : "External view (COR-safe)"}
            </span>
          </button>
        </div>

        <div className="mt-6">
          <FoiaTimeline
            events={request.events}
            steps={steps}
            currentStepKey={request.currentStepKey}
            mode={mode}
            renderAction={
              isInternal
                ? (stepKey) => {
                    const step = getStepDefinition(request.branch, stepKey);
                    if (!step) return null;
                    return (
                      <FoiaStepAction request={request} step={step} onComplete={() => handleStepComplete(stepKey)} />
                    );
                  }
                : null
            }
          />
        </div>
      </div>
    </FoiaPageShell>
  );
}
